import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { readFileSync } from 'node:fs';
import { personalPost, type PersonalRequestOptions } from '../../../lib/personal-client';
import { resolveContext, type ResolvedContext } from '../../../lib/global-opts';
import { CliError, ExitCode, ServerClientHttpError } from '../../../lib/errors';
import { printJson, sanitizeForTerminal } from '../../../lib/output';
import { PERSONAL_APPLICATIONS_BASE } from '@wport/core';
import { resolveMessage, validateBatchItems, type ApplyBatchItem } from './message-input';

interface ApplyFlags {
	resume?: string;
	message?: string;
	messageFile?: string;
	file?: string;
	confirm?: boolean;
}

/** 把 409/429 的結構化 body 轉成人類可讀訊息（保留 exit 3）。其餘錯誤原樣上拋。 */
function rethrowApplyError(err: unknown): never {
	if (err instanceof ServerClientHttpError) {
		const body = (err.body ?? null) as Record<string, unknown> | null;
		const code = body?.error_code;
		if (err.status === 409 && code === 'application_cooldown') {
			throw new CliError(`Application in cooldown. Next apply at: ${body?.next_apply_at ?? 'unknown'}`, ExitCode.ServerClientError);
		}
		if (err.status === 409 && code === 'job_not_available') {
			throw new CliError('Job is no longer available (closed or removed).', ExitCode.ServerClientError);
		}
		if (err.status === 429 && code === 'apply_daily_limit_reached') {
			throw new CliError(
				`Daily application limit reached (${body?.used}/${body?.limit}). Resets at: ${body?.resets_at ?? 'tomorrow'}`,
				ExitCode.ServerClientError
			);
		}
	}
	throw err;
}

export async function runPersonalApply(
	ctx: ResolvedContext,
	args: { encJobId: string; encResumeId: string; message: string; confirm: boolean }
): Promise<void> {
	if (!args.confirm) {
		throw new CliError(
			'Refusing to apply without --confirm (application is visible to the employer)',
			ExitCode.InvalidArgument
		);
	}
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	const body = { enc_job_id: args.encJobId, enc_resume_id: args.encResumeId, application_message: args.message };
	let result;
	try {
		result = await personalPost(opts, PERSONAL_APPLICATIONS_BASE, body, { idempotencyKey: randomUUID() });
	} catch (err) {
		rethrowApplyError(err);
	}
	const data = (result.body as { data?: Record<string, unknown> })?.data ?? {};
	if (ctx.format === 'json') {
		printJson(data);
		return;
	}
	process.stdout.write(`Applied. application: ${sanitizeForTerminal(String(data.enc_application_id ?? ''))}\n`);
	const q = data.quota as { used?: number; limit?: number; remaining?: number } | undefined;
	if (q) process.stdout.write(`Daily quota: ${q.used}/${q.limit} used, ${q.remaining} remaining\n`);
}

export function registerPersonalApplyCommand(parent: Command): void {
	parent
		.command('apply [job_enc_id]')
		.description('Apply to a job (single) or batch via --file (requires --confirm)')
		.option('--resume <enc_id>', 'resume enc_id to apply with (single mode)')
		.option('--message <text>', 'application message (1~2000 chars)')
		.option('--message-file <path>', 'read application message from file (use - for stdin)')
		.option('--file <path>', 'batch JSON file { "applications": [...] } (max 20)')
		.option('--confirm', 'confirm this outward-facing action')
		.action(async (jobEncId: string | undefined, flags: ApplyFlags, command: Command) => {
			const ctx = resolveContext(command);
			if (flags.file) {
				await runPersonalApplyBatch(ctx, { file: flags.file, confirm: flags.confirm === true });
				return;
			}
			if (!jobEncId || !flags.resume) {
				throw new CliError('single apply requires <job_enc_id> and --resume', ExitCode.InvalidArgument);
			}
			const message = resolveMessage({ message: flags.message, messageFile: flags.messageFile });
			await runPersonalApply(ctx, {
				encJobId: jobEncId,
				encResumeId: flags.resume,
				message,
				confirm: flags.confirm === true,
			});
		});
}

export async function runPersonalApplyBatch(
	ctx: ResolvedContext,
	args: { file: string; confirm: boolean }
): Promise<void> {
	if (!args.confirm) {
		throw new CliError(
			'Refusing to apply without --confirm (applications are visible to employers)',
			ExitCode.InvalidArgument
		);
	}
	let parsed: unknown;
	try {
		parsed = JSON.parse(readFileSync(args.file, 'utf8'));
	} catch {
		throw new CliError(`Cannot read/parse batch file: ${args.file}`, ExitCode.InvalidArgument);
	}
	const items: ApplyBatchItem[] = validateBatchItems((parsed as { applications?: unknown })?.applications);
	const opts: PersonalRequestOptions = { baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs };
	let result;
	try {
		result = await personalPost(opts, `${PERSONAL_APPLICATIONS_BASE}/batch`, { applications: items }, { idempotencyKey: randomUUID() });
	} catch (err) {
		rethrowApplyError(err);
	}
	const data = (result.body as { data?: Record<string, unknown> })?.data ?? {};
	if (ctx.format === 'json') {
		printJson(data);
		return;
	}
	const succeeded = (data.succeeded as unknown[]) ?? [];
	const failed = (data.failed as { index: number; enc_job_id: string; error_code: string }[]) ?? [];
	process.stdout.write(`Batch applied: ${succeeded.length} succeeded, ${failed.length} failed\n`);
	for (const f of failed) {
		process.stdout.write(`  #${f.index} ${sanitizeForTerminal(f.enc_job_id)}: ${sanitizeForTerminal(f.error_code)}\n`);
	}
}
